import React, { useEffect, useState } from 'react';
import { apiGet } from '../api';
import { DashboardLayout } from '../components/Layout';
import SavedPaymentMethods from '../components/SavedPaymentMethods';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { CreditCard, Receipt, Loader2, FileText, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const TYPE_LABELS = {
  class_pass_purchase: 'ClassPass',
  single_class_booking: 'Class Booking',
  membership_purchase: 'Membership',
  voucher_purchase: 'Voucher',
  payment: 'Payment',
};

const formatCurrency = (amount, currency = 'EUR') => {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount || 0);
  } catch {
    return `€${(amount || 0).toFixed(2)}`;
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'd MMM yyyy, HH:mm');
  } catch {
    return iso;
  }
};

const StatusBadge = ({ receipt }) => {
  const refunded = receipt.refunded_amount || 0;
  const total = receipt.amount || 0;
  if (refunded > 0 && refunded >= total) {
    return <Badge variant="destructive" data-testid="receipt-status">Refunded</Badge>;
  }
  if (refunded > 0) {
    return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="receipt-status">Partial Refund</Badge>;
  }
  if (receipt.status === 'completed') {
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="receipt-status">Paid</Badge>;
  }
  return <Badge variant="secondary" data-testid="receipt-status">{receipt.status}</Badge>;
};

const ReceiptDetailModal = ({ receipt, onClose }) => {
  if (!receipt) return null;
  return (
    <Dialog open={!!receipt} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="receipt-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Receipt
          </DialogTitle>
          <DialogDescription>
            {formatDate(receipt.created_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Description</p>
              <p className="font-medium">{receipt.description}</p>
            </div>
            <StatusBadge receipt={receipt} />
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Studio</p>
              <p>{receipt.studio_name}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Type</p>
              <p>{TYPE_LABELS[receipt.type] || receipt.type}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Payment</p>
              <p className="capitalize">{(receipt.payment_method_used || 'Card').replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Receipt ID</p>
              <p className="font-mono text-xs break-all">{receipt.id}</p>
            </div>
          </div>
          <div className="border-t pt-3 space-y-1.5 text-sm">
            {receipt.original_amount > receipt.amount && (
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>{formatCurrency(receipt.original_amount, receipt.currency)}</span>
              </div>
            )}
            {receipt.discount_amount > 0 && (
              <div className="flex justify-between text-emerald-600">
                <span>Discount{receipt.voucher_code ? ` (${receipt.voucher_code})` : ''}</span>
                <span>− {formatCurrency(receipt.discount_amount, receipt.currency)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span>Total Paid</span>
              <span>{formatCurrency(receipt.amount, receipt.currency)}</span>
            </div>
            {(receipt.refunds || []).length > 0 && (
              <div className="border-t pt-2 mt-2 space-y-1">
                <p className="text-xs uppercase text-muted-foreground">Refunds</p>
                {receipt.refunds.map((r, idx) => (
                  <div key={idx} className="flex justify-between text-amber-700">
                    <span>{formatDate(r.created_at)}</span>
                    <span>− {formatCurrency(r.amount, receipt.currency)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button onClick={onClose} className="w-full mt-2" data-testid="receipt-detail-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BillingPage = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiGet('/api/client/receipts');
        if (mounted) setReceipts(data || []);
      } catch (e) {
        console.error('Failed to load receipts:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="billing-page">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <CreditCard className="w-8 h-8" />
            Billing
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage saved cards and review your payment history.
          </p>
        </div>

        <SavedPaymentMethods />

        <Card data-testid="receipts-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Receipts &amp; Transactions
            </CardTitle>
            <CardDescription>
              Every payment you&rsquo;ve made — classes, ClassPasses, memberships, and refunds.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : receipts.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground">No receipts yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your purchases will appear here.
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {receipts.map((r, i) => (
                  <button
                    key={r.id}
                    onClick={() => setSelected(r)}
                    data-testid={`receipt-row-${i}`}
                    className="w-full flex items-center gap-4 py-4 px-2 hover:bg-muted/50 transition-colors text-left rounded-lg"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Receipt className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{r.description}</p>
                        <Badge variant="outline" className="text-xs">
                          {TYPE_LABELS[r.type] || r.type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {r.studio_name} • {formatDate(r.created_at)}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold tabular-nums">
                        {formatCurrency(r.amount, r.currency)}
                      </p>
                      <StatusBadge receipt={r} />
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="p-4 rounded-lg border bg-muted/30">
          <h3 className="font-medium mb-2">Why save a card?</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Faster checkout</strong> &mdash; skip card entry every time</li>
            <li>• <strong>Auto-renewal</strong> &mdash; keep memberships active</li>
            <li>• <strong>Secure storage</strong> &mdash; cards are tokenised, never stored on our servers</li>
          </ul>
        </div>
      </div>

      <ReceiptDetailModal receipt={selected} onClose={() => setSelected(null)} />
    </DashboardLayout>
  );
};

export default BillingPage;
